import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  MaxCountsResultData,
  PaginatedQueryWrapper,
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import { DatabasesDisplayGridRowDataFragment } from '../../../gen-src/queries/DatabasesDisplayGridRowDataFragment';
import {
  VirtualConnectionUpstreamDatabasesFilteredByTableQuery,
  VirtualConnectionUpstreamDatabasesFilteredByTableQuery_virtualConnections_tablesConnection_nodes_upstreamDatabasesConnection_nodes,
  VirtualConnectionUpstreamDatabasesFilteredByTableQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionUpstreamDatabasesFilteredByTableQuery';
import {
  VirtualConnectionUpstreamDatabasesQuery,
  VirtualConnectionUpstreamDatabasesQuery_virtualConnections_upstreamDatabasesConnection_nodes,
  VirtualConnectionUpstreamDatabasesQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionUpstreamDatabasesQuery';
import { DatabaseSortOrder } from '../../../gen-src/queries/globalTypes';
import { VirtualConnectionUpstreamDatabasesFilteredByTableQueryGql } from '../../queries/VirtualConnectionUpstreamDatabasesFilteredByTableQueryGql';
import { VirtualConnectionUpstreamDatabasesQueryGql } from '../../queries/VirtualConnectionUpstreamDatabasesQueryGql';
import { getRowDataWithMaxCountsForDatabase } from '../database/functions/DatabaseRowDataFunctions';
import { DatabasesDisplayGridRowData } from '../database/types/DatabasesDisplayGridRowData';
import { DatabasesDisplayGrid } from '../sharedDisplayGrid/DatabasesDisplayGrid';
import { DatabasesValidSortFields } from '../sorting/sortProviders/DatabasesGridSortProvider';


type QueryVariables = VirtualConnectionUpstreamDatabasesQueryVariables;
type QueryResults = VirtualConnectionUpstreamDatabasesQuery;
type QueryResultListNode = VirtualConnectionUpstreamDatabasesQuery_virtualConnections_upstreamDatabasesConnection_nodes;
type GridRowData = DatabasesDisplayGridRowData;

type QueryVariablesWithFilter = VirtualConnectionUpstreamDatabasesFilteredByTableQueryVariables;
type QueryResultsWithFilter = VirtualConnectionUpstreamDatabasesFilteredByTableQuery;
type QueryResultListNodeWithFilter =
  VirtualConnectionUpstreamDatabasesFilteredByTableQuery_virtualConnections_tablesConnection_nodes_upstreamDatabasesConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.virtualConnections && resultData.virtualConnections[0] &&
    resultData.virtualConnections[0].upstreamDatabasesConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const virtualConnection = resultData && resultData.virtualConnections && resultData.virtualConnections[0];
  const virtualConnectionTables = virtualConnection && virtualConnection.tablesConnection && virtualConnection.tablesConnection.nodes;
  const database = virtualConnectionTables && virtualConnectionTables[0];
  return database ? database.upstreamDatabasesConnection : null;
}

function selectMaxCountsResult(resultData: QueryResults): MaxCountsResultData {
  return resultData && resultData.virtualConnections && resultData.virtualConnections[0];
}

function selectMaxCountsResultForFilteredQuery(resultData: QueryResultsWithFilter): MaxCountsResultData | null {
  const virtualConnection = resultData && resultData.virtualConnections && resultData.virtualConnections[0];
  const virtualConnectionTables = virtualConnection && virtualConnection.tablesConnection && virtualConnection.tablesConnection.nodes;
  const database = virtualConnectionTables && virtualConnectionTables[0];
  return database;
}

function getRowDataForDatabase(
  table: DatabasesDisplayGridRowDataFragment,
  resultData: QueryResults,
): DatabasesDisplayGridRowData {
  return getRowDataWithMaxCountsForDatabase(table, selectMaxCountsResult(resultData));
}

function getRowDataForDatabaseForFilteredQuery(
  table: DatabasesDisplayGridRowDataFragment,
  resultData: QueryResultsWithFilter,
): DatabasesDisplayGridRowData {
  return getRowDataWithMaxCountsForDatabase(table, selectMaxCountsResultForFilteredQuery(resultData));
}

export type VirtualConnectionUpstreamDatabasesQueryGridProps = GridSortProps<DatabasesValidSortFields> & {
  readonly vizportalUrlId: string;
  readonly filteredTableId: string | null;
  readonly rowCount: number | null;
};

export function VirtualConnectionUpstreamDatabasesQueryGrid(props: VirtualConnectionUpstreamDatabasesQueryGridProps): JSX.Element {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);

  const emptyGridMessage = Messages.EmptyUpstreamDatabasesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyUpstreamFilteredDatabasesLineageGridMessage();

  return props.filteredTableId ? (
    // Query with Selected Database as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionUpstreamDatabasesFilteredByTableQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        selectedTableId: props.filteredTableId,
        orderBy: props.orderBy as DatabaseSortOrder,
        publishedConnectionsLineageEnabled
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForDatabaseForFilteredQuery}
    >
      {({ rowStore }) =>
        <DatabasesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      }
    </PaginatedQueryWrapper>
  ) : (
    // Query without Selection
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionUpstreamDatabasesQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        orderBy: props.orderBy as DatabaseSortOrder,
        publishedConnectionsLineageEnabled
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForDatabase}
    >
      {({ rowStore }) =>
        <DatabasesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
    </PaginatedQueryWrapper>
  );
}
